import React, {
    useCallback,
    useEffect,
    useMemo,
    useState
} from 'react';

import axios from 'axios';

import {
    FaPlus,
    FaSave,
} from 'react-icons/fa';

import { toast } from 'react-toastify';
import { useLocation } from 'react-router-dom';

import { useStock } from '../../../context/StockProvider';

import Footer from '../../Footer';
import Navbar from '../../Navbar';
import OutletSearch from '../../SelectSearch/OutletSearch';
import PageStatus from '../../PageStatus/PageStatus';
import ConversionFactorInput from './ConversionFactorInput';
import ConversionFactorTable from './ConversionFactorTable';

const selectStyles = {
    control: (base, state) => ({
        ...base,
        minHeight: "38px",
        height: "38px",
        fontSize: "0.875rem",
        borderColor: state.isFocused
            ? "#2563eb"
            : "#d1d5db",
        boxShadow: "none",
        borderRadius: "8px",
        "&:hover": {
            borderColor: "#2563eb",
        },
    }),

    valueContainer: (base) => ({
        ...base,
        padding: "0 8px",
    }),

    input: (base) => ({
        ...base,
        margin: 0,
        padding: 0,
    }),

    indicatorsContainer: (base) => ({
        ...base,
        height: "38px",
    }),

    menu: (base) => ({
        ...base,
        zIndex: 50,
        fontSize: "0.875rem",
    }),

    option: (
        base,
        { isFocused, isSelected }
    ) => ({
        ...base,
        backgroundColor: isSelected
            ? "#2563eb"
            : isFocused
                ? "#eff6ff"
                : "white",
        color: isSelected
            ? "white"
            : "#111827",
    }),
};

const editableClass =
    "w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-600";

const createEmptyRow = () => ({
    finishedGoods: null,
    productionRecipe: null,
    factor: "",
    linkedMenuItem: null
});

const ConversionFactor = () => {
    const baseUrl = process.env.REACT_APP_BASE_URL;
    const token = localStorage.getItem("token");
    const [selectedOutlet, setSelectedOutlet] = useState(null);

    const location = useLocation();
    const incomingItemNames = useMemo(
        () => location.state?.itemNames ?? [],
        [location.state?.itemNames]
    );

    useEffect(() => {
    }, [incomingItemNames]);

    const { stockItems, stockError, stockLoading } = useStock();
    const [recipes, setRecipes] = useState([]);
    const [menuItems, setMenuItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [menuLoading, setMenuLoading] = useState(false);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [error, setError] = useState(null);
    const [menuError, setMenuError] = useState(null);
    const [rows, setRows] = useState([createEmptyRow()]);
    const [prefillApplied, setPrefillApplied] = useState(false);

    const [data, setData] = useState([]);
    const [dataLoading, setDataLoading] = useState(false);
    const [dataError, setDataError] = useState(null);

    const fetchConversionFactors = useCallback(async () => {
        if (!selectedOutlet) return;

        try {
            setDataLoading(true);
            setDataError(null);

            const res = await axios.get(`${baseUrl}/conversionfactor?outlet=${encodeURIComponent(selectedOutlet)}`);

            setData(res.data || []);
        } catch (err) {
            console.error(err);
            setDataError(
                err.response?.data?.message ||
                "Failed to fetch conversion factors"
            );
        } finally {
            setDataLoading(false);
        }
    }, [selectedOutlet, baseUrl]);

    const fetchRecipes = useCallback(async () => {
        if (!selectedOutlet) return;

        try {
            setRecipes([]);
            setLoading(true);

            const res = await axios.get(`${baseUrl}/recipes?outlet=${encodeURIComponent(selectedOutlet)}`);

            setRecipes(res.data || []);
        } catch (err) {
            console.error(err);

            toast.error(
                err.response?.data?.error ||
                err.response?.data?.message ||
                "Failed to fetch recipes"
            );

            setError(
                err.response?.data?.error ||
                err.response?.data?.message ||
                "Failed to fetch production recipes"
            );
        } finally {
            setLoading(false);
        }
    }, [selectedOutlet, baseUrl]);

    const fetchMenuItems = useCallback(async () => {
        if (!selectedOutlet || !token) return;

        try {
            setMenuItems([]);
            setMenuLoading(true);

            const res = await axios.post(`${baseUrl}/menu-details`, {
                token,
                outlet: selectedOutlet,
            });

            setMenuItems(res.data || []);
        } catch (err) {
            console.error(err);

            setMenuError(
                err.response?.data?.error ||
                err.response?.data?.message ||
                "Failed to fetch menu item"
            );
        } finally {
            setMenuLoading(false);
        }
    }, [selectedOutlet, token, baseUrl]);

    useEffect(() => {
        if (!token || !selectedOutlet) return;

        fetchRecipes();
        fetchConversionFactors();
        fetchMenuItems();
        setRows([createEmptyRow()]);
        setPrefillApplied(false);
    }, [token, selectedOutlet, fetchRecipes, fetchConversionFactors, fetchMenuItems]);

    const finishedGoodsOptions = useMemo(() => {
        return stockItems
            ?.filter(
                (item) =>
                    item.GroupName?.toLowerCase() ===
                    "finished goods"
            )
            ?.map((item) => ({
                label: `${item.ItemName} (${item.UOM})`,
                value: item.ItemName,
                item
            }));
    }, [stockItems]);

    const productionRecipeOptions = useMemo(() => {
        return recipes
            ?.filter(
                recipe =>
                    recipe.recipe_type ===
                    "Production"
            )
            ?.map(recipe => ({
                label: recipe.name,
                value: recipe.id,
                recipe
            }));

    }, [recipes]);

    const menuItemOptions = useMemo(() => {
        return menuItems?.map((item) => ({
            label: `${item.Name} (${item.Description})`,
            value: item.Name,
            item
        }));
    }, [menuItems]);

    // Auto-populate rows from item names passed via navigation state (e.g. after
    // adding new production stock items), matched against finished goods.
    useEffect(() => {
        if (prefillApplied) return;
        if (!incomingItemNames.length) return;
        if (!finishedGoodsOptions?.length) return;

        const matchedRows = incomingItemNames
            .map((name) => {
                const match = finishedGoodsOptions.find(
                    (opt) => opt.item.ItemName === name
                );
                return match
                    ? { ...createEmptyRow(), finishedGoods: match.item }
                    : null;
            })
            .filter(Boolean);

        if (matchedRows.length) {
            setRows(matchedRows);
        }

        setPrefillApplied(true);
    }, [incomingItemNames, finishedGoodsOptions, prefillApplied]);

    const handleRowChange = (index, field, value) => {
        const updated = [...rows];

        updated[index][field] = value;

        if (field === "finishedGoods") {
            updated[index].linkedMenuItem = null;
        }

        setRows(updated);
    };

    const addRow = () => {
        setRows(prev => [
            ...prev,
            createEmptyRow()
        ]);
    };

    const removeRow = (index) => {
        if (rows.length === 1) return;

        setRows(prev =>
            prev.filter(
                (_, i) => i !== index
            )
        );
    };

    const getUnitCost = (row) => {
        if (row.productionRecipe && row.factor > 0) {
            return Number(row.productionRecipe.sellingprice || 0) / Number(row.factor);
        }
        return 0;
    };

    const getMenuOptionsForRow = (row) => {
        const department = row.finishedGoods?.Department;

        if (!department) return menuItemOptions;

        return menuItemOptions.filter(
            (opt) =>
                opt.item.Type?.toLowerCase() === department.toLowerCase()
        );
    };

    const handleSaveConversionFactors = () => {
        const invalidRow = rows.some(
            row => !row.finishedGoods || !row.productionRecipe || !row.factor
        );

        if (invalidRow) {
            toast.error("Please complete all rows");
            return;
        }

        toast.info(
            <div className="p-2">
                <p className="mb-3 text-white">Are you sure you want to save conversion factors?</p>
                <div className="flex justify-between gap-2">
                    <button
                        className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
                        onClick={async () => {
                            toast.dismiss();
                            try {
                                setSubmitLoading(true);

                                await axios.post(
                                    `${baseUrl}/conversionfactor`, {
                                    token,
                                    outlet: selectedOutlet,
                                    conversion_factors: rows.map(row => ({
                                        production_item_id: row.finishedGoods.id,
                                        production_recipe_id: row.productionRecipe.id,
                                        factor: parseFloat(row.factor)
                                    }))
                                });

                                toast.success("Conversion factors saved successfully");
                                fetchConversionFactors();

                                rows.forEach((row, index) => {
                                    if (row.linkedMenuItem) {
                                        handleSaveMenuRecipe(index);
                                    }
                                });

                                setRows([createEmptyRow()]);

                            } catch (err) {
                                console.error(err);
                                toast.error(
                                    err.response?.data?.error ||
                                    err.response?.data?.message ||
                                    "Failed to save conversion factors"
                                );
                            } finally {
                                setSubmitLoading(false);
                            }
                        }}
                    >
                        Yes, Save
                    </button>
                    <button
                        className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                        onClick={() => toast.dismiss()}
                    >
                        Cancel
                    </button>
                </div>
            </div>,
            {
                autoClose: false,
                closeButton: false,
                toastId: "save-conversion-factors-confirmation"
            }
        );
    };

    const handleSaveMenuRecipe = async (index) => {
        const row = rows[index];

        if (!row.finishedGoods || !row.productionRecipe || !row.factor || !row.linkedMenuItem) {
            toast.error("Please complete production item, recipe, factor and menu item link before saving");
            return;
        }

        const menuItem = row.linkedMenuItem;
        const unitCost = getUnitCost(row);

        try {
            const payload = {
                token,
                outlet: selectedOutlet,
                name: menuItem.Name,
                cost_price: unitCost,
                selling_price: unitCost,
                cost_percent: 100,
                items: [
                    {
                        name: row.finishedGoods.ItemName,
                        rate: unitCost,
                        uom: row.finishedGoods.UOM,
                        new_uom: row.productionRecipe.uom || row.finishedGoods.UOM,
                        quantity: "1",
                        cost: unitCost,
                        unit: row.finishedGoods.unit_definitions?.[0]?.unit
                    }
                ],
                sub_recipes: [],
                item_type: menuItem.Type,
                recipe_type: "Menu",
                GroupName: menuItem.Description
            };

            await axios.post(`${baseUrl}/recipe`, payload);
            toast.success("Recipe saved successfully!");

        } catch (error) {
            console.error('Error saving recipe:', error);
            toast.error(
                error?.response?.data?.error ||
                error?.response?.data?.message ||
                'Failed to save recipe'
            );
        }
    };

    return (
        <div className="flex flex-col min-h-screen bg-gray-50">

            <Navbar />

            <div className="flex-1 p-3">
                <div className="flex justify-center mb-2.5">
                    <OutletSearch
                        selectedOutlet={selectedOutlet}
                        setSelectedOutlet={setSelectedOutlet}
                        token={token}
                    />
                </div>


                <div className='flex justify-center gap-6'>
                    <div className="w-[59%]">
                        <ConversionFactorTable
                            baseUrl={baseUrl}
                            selectedOutlet={selectedOutlet}
                            token={token}
                            loading={dataLoading}
                            error={dataError}
                            data={data}
                            setData={setData}
                            selectStyles={selectStyles}
                        />
                    </div>

                    <div className="w-[39%]">
                        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-3">
                            <div className="flex items-center justify-between mb-2">
                                <div className="text-base font-semibold text-gray-800">
                                    Unit Production Costing
                                </div>

                                <button
                                    type="button"
                                    onClick={addRow}
                                    className="flex items-center gap-1.5 px-3 py-1.5 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 text-sm font-medium"
                                >
                                    <FaPlus size={12} />
                                    Add Row
                                </button>
                            </div>

                            <PageStatus
                                loading={stockLoading}
                                error={stockError}
                                loadingText="Loading stock items..."
                            />

                            <PageStatus
                                loading={loading}
                                error={error}
                                loadingText="Loading production items..."
                            />

                            {!stockLoading && !stockError && !loading && !error && (
                                <ConversionFactorInput
                                    rows={rows}
                                    addRow={addRow}
                                    removeRow={removeRow}
                                    handleRowChange={handleRowChange}
                                    loading={loading}
                                    productionRecipeOptions={productionRecipeOptions}
                                    finishedGoodsOptions={finishedGoodsOptions}
                                    getMenuOptionsForRow={getMenuOptionsForRow}
                                    menuLoading={menuLoading}
                                    menuError={menuError}
                                    selectStyles={selectStyles}
                                    editableClass={editableClass}
                                />
                            )}

                            <div className="mt-2 flex justify-end">
                                <button
                                    type="button"
                                    onClick={handleSaveConversionFactors}
                                    disabled={submitLoading || stockLoading || stockError || loading || error}
                                    className="flex items-center gap-2 px-3 py-1.5 text-sm rounded-md bg-blue-100 text-blue-700 hover:bg-blue-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <FaSave size={12} />

                                    {submitLoading
                                        ? "Saving..."
                                        : "Save Conversion Factors"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

            </div>

            <Footer />
        </div>
    );
};

export default ConversionFactor;